import { useState } from "react";
import { MessageSquarePlus, X, Send, Star, Mail, Paperclip, CheckCircle2, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type FbType = "bug" | "rating" | "feature";

const SUPPORT_EMAIL = "info@inquira.online";

export function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FbType>("rating");
  const [rating, setRating] = useState<number>(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [ticket, setTicket] = useState<{ id: string; type: FbType } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!user) return null;

  async function uploadScreenshot(userId: string): Promise<string | null> {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Screenshot must be under 5MB");
      throw new Error("too large");
    }
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Screenshot must be a PNG, JPEG, or WebP image");
      throw new Error("bad type");
    }
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("feedback-screenshots")
      .upload(path, file, { cacheControl: "3600", upsert: false });
    if (error) throw error;
    return path;
  }

  async function submit() {
    const trimmed = message.trim();
    setNotice(null);
    if (trimmed.length < 5) {
      toast.error("Please write at least 5 characters");
      return;
    }
    if (trimmed.length > 4000) {
      toast.error("Message must be under 4000 characters");
      return;
    }
    if (type === "rating" && rating === 0) {
      toast.error("Please pick a star rating");
      return;
    }
    setSubmitting(true);
    let screenshot_url: string | null = null;
    try {
      screenshot_url = await uploadScreenshot(user!.id);
    } catch (e) {
      setSubmitting(false);
      return;
    }
    const { data, error } = await supabase.rpc("submit_feedback" as never, {
      _type: type,
      _message: trimmed,
      _rating: type === "rating" ? rating || null : null,
      _page_url: window.location.pathname,
      _screenshot_url: screenshot_url,
    } as never);
    setSubmitting(false);
    const res = data as
      | { status: string; id?: string; error?: string; spam_score?: number }
      | null;
    if (error || !res) {
      toast.error(error?.message ?? "Could not send feedback");
      return;
    }
    if (res.status === "invalid" || res.status === "rate_limited") {
      setNotice(res.error ?? "Could not send feedback");
      toast.error(res.error ?? "Could not send feedback");
      return;
    }
    if (res.status === "duplicate") {
      setNotice("You already sent this exact message — we're on it.");
      setTicket({ id: res.id!, type });
      setMessage("");
      setFile(null);
      return;
    }
    if (res.status === "flagged") {
      setNotice("Submitted for review — your message was flagged by our spam filter.");
    }
    // Also hand off to the support inbox so bugs/feature requests reach info@inquira.online.
    if (type !== "rating" && res.status === "ok") {
      const subject =
        type === "bug" ? "Inquira bug report" : "Inquira feature request";
      const body = `Ticket #${res.id}\n\n${trimmed}\n\n— sent from ${window.location.pathname}`;
      const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
      window.open(href, "_blank", "noopener,noreferrer");
    }
    setTicket({ id: res.id!, type });
    setMessage("");
    setRating(0);
    setFile(null);
  }

  function reset() {
    setTicket(null);
    setNotice(null);
    setOpen(false);
  }

  return (
    <>
      <button
        aria-label="Send feedback"
        onClick={() => setOpen(true)}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90"
      >
        <MessageSquarePlus className="h-5 w-5" />
      </button>
      {open && (
        <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
          <div className="modal-panel w-full max-w-md rounded-2xl bg-card p-5 shadow-xl">
            {ticket ? (
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 text-green-600">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold">Thanks — we got it!</h3>
                {notice && (
                  <p className="mt-2 rounded-lg bg-amber-500/10 p-2 text-xs text-amber-600">{notice}</p>
                )}
                <p className="mt-1 text-sm text-muted-foreground">
                  Your {ticket.type === "rating" ? "rating" : "message"} was sent to{" "}
                  <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
                <div className="mt-4 rounded-lg border border-dashed border-border bg-background p-3 text-left">
                  <div className="text-xs text-muted-foreground">Ticket ID</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <code className="truncate text-xs">{ticket.id}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(ticket.id);
                        toast.success("Ticket ID copied");
                      }}
                      className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                </div>
                <button
                  onClick={reset}
                  className="mt-4 w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                >
                  Done
                </button>
              </div>
            ) : (
            <>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Send feedback</h3>
              <button onClick={() => setOpen(false)} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3 flex gap-2">
              {(["rating", "bug", "feature"] as FbType[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm capitalize ${
                    type === t ? "border-primary bg-primary/10" : "border-border"
                  }`}
                >
                  {t === "rating" ? "Rate" : t}
                </button>
              ))}
            </div>
            {type === "rating" && (
              <div className="mb-3 flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    aria-label={`Rate ${n} stars`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        rating >= n ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
                      }`}
                    />
                  </button>
                ))}
              </div>
            )}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                type === "bug"
                  ? "What went wrong?"
                  : type === "feature"
                  ? "What would you like to see?"
                  : "Tell us what you think"
              }
              maxLength={4000}
              rows={4}
              className="w-full rounded-lg border border-border bg-background p-3 text-sm"
            />
            <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{notice ?? "Minimum 5 characters"}</span>
              <span>{message.trim().length}/4000</span>
            </div>
            <label className="mt-2 flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-dashed border-border bg-background p-2.5 text-xs text-muted-foreground hover:bg-muted/50">
              <span className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                {file ? file.name : "Attach a screenshot (optional, ≤5MB)"}
              </span>
              {file && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setFile(null);
                  }}
                  className="text-xs underline"
                >
                  Remove
                </button>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={submitting}
                onClick={submit}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {submitting ? "Sending..." : "Send"}
              </button>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5" />
              <span>
                Prefer email?{" "}
                <a
                  className="underline"
                  href={`mailto:${SUPPORT_EMAIL}`}
                >
                  {SUPPORT_EMAIL}
                </a>
              </span>
            </div>
            </>
            )}
          </div>
        </div>
      )}
    </>
  );
}